/**
 * PURPOSE: Visor grande (por encima de la planilla) con el PDF de la planilla FEDUP del partido, sin descargarlo.
 * RESPONSIBILITIES:
 *  - Sí: pedir el PDF al abrir (datos frescos) y mostrarlo con el visor nativo del navegador mediante un blob URL.
 *  - Sí: cabecera con título en negrita + hora de la última actualización (gris), "Actualizar" (solo icono), zoom − / + y "Cerrar";
 *    Esc y clic fuera también cierran; estado de carga; errores claros con "Reintentar".
 *  - Sí: zoom y scroll propios (el visor nativo no se controla desde fuera): el iframe mide `ancho × zoom` y la altura de la hoja dentro
 *    de un contenedor con scroll. Cada apertura parte del zoom y la posición por defecto del deporte; "Actualizar" conserva ambos.
 *  - Sí: respaldo con "Abrir en pestaña nueva" cuando el navegador no puede mostrar PDF embebido (sin botones de zoom).
 *  - Sí: role="dialog", aria-modal, foco inicial en "Cerrar" y marca de visor abierto para que los atajos de la planilla no se disparen.
 *  - Sí (FTR-0062, solo si el deporte pasa `signatures`, hoy vóley): selector "Firmas previas | Firmas finales" a la izquierda de
 *    "Actualizar", casillas sobre el PDF (overlay; solo con el visor de Chrome, cuya geometría se midió) o panel de tarjetas (resto de
 *    navegadores y sin visor embebido), y el pad de firma. Tras firmar se regenera el PDF conservando zoom y scroll.
 *  - No: no modifica el estado de la planilla; un partido en juego produce un PDF parcial y eso no es un error.
 * COLLABORATORS:
 *  - FedupPreviewButton.jsx, useFedupPreview.js, fedupPreview.utils.js, fedupSignatures/*
 * NOTES:
 *  - Esc no llega a la página si el foco está dentro del PDF (el iframe consume sus teclas): ahí cierran el botón o el clic fuera.
 *  - El contenido (`FedupPreviewContent`) solo existe mientras el modal está abierto: el zoom y el scroll se reinician solos en cada apertura.
 *  - Sin `signatures` el visor es exactamente el de FTR-0058 (no se pide nada más ni cambia el DOM).
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/FedupPreviewModal.jsx
 */

import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import { FiAlertTriangle, FiExternalLink, FiLoader, FiMinus, FiPlus, FiRefreshCw, FiX } from "react-icons/fi";

import { useFedupPreview } from "./useFedupPreview.js";
import {
  FEDUP_PREVIEW_OPEN_ATTR,
  FEDUP_ZOOM_MAX,
  FEDUP_ZOOM_MIN,
  buildPreviewViewerSrc,
  canEmbedPdf,
  clampZoom,
  computeFrameSize,
  computeInitialScrollTop,
  getPreviewView,
  stepZoom,
} from "./fedupPreview.utils.js";
import FedupSignatureModeSelector from "./fedupSignatures/FedupSignatureModeSelector.jsx";
import FedupSignatureOverlay from "./fedupSignatures/FedupSignatureOverlay.jsx";
import FedupSignaturePanel from "./fedupSignatures/FedupSignaturePanel.jsx";
import FedupSignaturePad from "./fedupSignatures/FedupSignaturePad.jsx";
import { useFedupSignatureController } from "./fedupSignatures/useFedupSignatureController.js";
import {
  SIGNATURE_UI_MESSAGES,
  boxToFrameRect,
  chromePdfPageMap,
  detectPdfViewerEngine,
  getModeInfo,
  scrollTargetFor,
  slotsOfMode,
  unionRect,
} from "./fedupSignatures/fedupSignatures.utils.js";

// Solo puede haber un visor abierto a la vez, así que el id del título es fijo (lo usa aria-labelledby del diálogo).
const TITLE_ID = "fedup-preview-title";

const ICON_BTN =
  "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-base text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40";
const PRIMARY_BTN =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-4 text-xs font-bold text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400";

function formatUpdatedAt(date) {
  if (!date) return "";
  try {
    return date.toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "";
  }
}

/** Ancho útil (clientWidth) del contenedor con scroll, seguido con ResizeObserver. */
function useElementWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => setWidth(el.clientWidth || 0);
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/** Ver el comentario de `useOverlay`: el panel de tarjetas es el modo principal de firma. */
const OVERLAY_ENABLED = false;

function FedupPreviewContent({ onClose, matchId, loadPdf, sport, signatures = null, container = null }) {
  const closeRef = useRef(null);
  const scrollRef = useRef(null);
  const initialScrollDoneRef = useRef(false);
  const pendingCenterRef = useRef(null);

  const view = getPreviewView(sport);
  const { status, url, error, updatedAt, reload } = useFedupPreview({ open: true, matchId, loadPdf });
  const embedSupported = canEmbedPdf();
  const isLoading = status === "loading";
  const showViewer = Boolean(url) && status !== "error";
  const showFrame = showViewer && embedSupported;

  const [zoom, setZoom] = useState(() => clampZoom(view.zoom));
  const containerWidth = useElementWidth(scrollRef);
  const frame = computeFrameSize(containerWidth, zoom, view.pageRatio);

  // ---- FTR-0062: firmas digitales (solo si el deporte entrega `signatures`) ----
  const sig = useFedupSignatureController({ service: signatures, matchId, onPdfChanged: reload });
  const engine = useMemo(() => detectPdfViewerEngine(), []);
  const signaturesOn = Boolean(signatures) && sig.available && showViewer;
  const activeMode = signaturesOn ? sig.mode : null;
  // Overlay DESACTIVADO (FTR-0062, 2026-10-11): el visor PDF nativo es un frame que la página no puede leer; un Ctrl+rueda o un
  // pellizco sobre el PDF cambia su zoom/scroll interno sin aviso y los recuadros quedaban descuadrados en el Chrome real del dueño.
  // El panel de tarjetas es el modo principal de firma en todos los navegadores. Reactivarlo exige dibujar el PDF nosotros (pdf.js).
  const useOverlay = OVERLAY_ENABLED && Boolean(activeMode) && showFrame && engine === "chrome";
  const usePanel = Boolean(activeMode) && !useOverlay;
  const [frameEpoch, setFrameEpoch] = useState(0);
  const pointerVisitedFrameRef = useRef(false);
  const frameWrapRef = useRef(null);
  const pendingModeScrollRef = useRef(null);
  const clearPreviewsRef = useRef(sig.clearPreviews);
  clearPreviewsRef.current = sig.clearPreviews;
  const pageMap = useMemo(
    () => (signatures && frame.width ? chromePdfPageMap(frame.width, sig.status?.sheet?.width || 1190) : null),
    [signatures, frame.width, sig.status]
  );

  // Al elegir un modo: si el puntero pasó por el PDF (pudo cambiar el zoom nativo con Ctrl+rueda) se recarga el iframe con el mismo
  // blob (vuelve a "ajustado al ancho"), y el visor se desplaza a la zona del modo.
  useEffect(() => {
    const act = sig.activation;
    if (!act.n || !signaturesOn) return;
    if (act.fromNone && pointerVisitedFrameRef.current) {
      pointerVisitedFrameRef.current = false;
      setFrameEpoch((n) => n + 1);
    }
    pendingModeScrollRef.current = act.mode;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo cuando se elige un modo
  }, [sig.activation.n]);

  useLayoutEffect(() => {
    const mode = pendingModeScrollRef.current;
    const el = scrollRef.current;
    const wrap = frameWrapRef.current;
    if (!mode || !el || !wrap || !pageMap || !sig.status) return;
    pendingModeScrollRef.current = null;
    const rect = unionRect(slotsOfMode(sig.status, mode).map((s) => boxToFrameRect(s.box, pageMap)));
    const target = scrollTargetFor(rect, {
      frameLeft: wrap.offsetLeft,
      frameTop: wrap.offsetTop,
      clientWidth: el.clientWidth,
      clientHeight: el.clientHeight,
      scrollWidth: el.scrollWidth,
      scrollHeight: el.scrollHeight,
    });
    if (!target) return;
    let reduce = false;
    try {
      reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      reduce = false;
    }
    if (typeof el.scrollTo === "function") el.scrollTo({ left: target.left, top: target.top, behavior: reduce ? "auto" : "smooth" });
    else {
      el.scrollLeft = target.left;
      el.scrollTop = target.top;
    }
  }, [sig.activation.n, pageMap, sig.status]);

  // El foco de entrada es "Cerrar": la trampa de foco del modal lo deja en el contenedor, así que se asienta después.
  useEffect(() => {
    const timer = window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, []);

  // El visor de PDF del navegador suele tomar el foco al cargar: si lo hizo, se devuelve a "Cerrar" para que Esc siga funcionando.
  // FTR-0062: con el PDF regenerado a la vista se retiran las firmas mostradas al instante (un respiro para que el visor pinte).
  const handleFrameLoad = useCallback((event) => {
    const active = document.activeElement;
    if (active === event.currentTarget || active === document.body) closeRef.current?.focus();
    window.setTimeout(() => clearPreviewsRef.current?.(), 700);
  }, []);

  const changeZoom = useCallback((next) => {
    const el = scrollRef.current;
    if (el && el.scrollWidth > 0 && el.scrollHeight > 0) {
      // Se conserva el punto que el planillero tiene en el centro de la vista.
      pendingCenterRef.current = {
        x: (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth,
        y: (el.scrollTop + el.clientHeight / 2) / el.scrollHeight,
      };
    }
    setZoom((current) => clampZoom(typeof next === "function" ? next(current) : next));
  }, []);

  // Tras un cambio de zoom: recolocar el scroll alrededor del mismo punto. Solo actúa si hay un zoom pendiente (nunca al actualizar).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const pending = pendingCenterRef.current;
    if (!el || !pending) return;
    pendingCenterRef.current = null;
    el.scrollLeft = Math.max(0, pending.x * el.scrollWidth - el.clientWidth / 2);
    el.scrollTop = Math.max(0, pending.y * el.scrollHeight - el.clientHeight / 2);
  }, [zoom, frame.width, frame.height]);

  // Posición inicial (p. ej. vóley: el recuadro del formulario pegado arriba): SOLO la primera vez que el PDF está listo tras abrir.
  useLayoutEffect(() => {
    if (initialScrollDoneRef.current) return;
    const el = scrollRef.current;
    if (!showFrame || !el || !frame.width) return;
    initialScrollDoneRef.current = true;
    el.scrollTop = computeInitialScrollTop(frame.width, view.initialTop);
  }, [showFrame, frame.width, view.initialTop]);

  // Ctrl + rueda sobre el contenedor (fuera del PDF) cambia el zoom del visor en lugar del zoom de la página; dentro del PDF
  // actúa el visor nativo, que sigue funcionando.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (event) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      changeZoom((current) => stepZoom(current, event.deltaY < 0 ? 1 : -1));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [changeZoom]);

  const updatedLabel = formatUpdatedAt(updatedAt);

  return (
    <>
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 py-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
          <h2 id={TITLE_ID} className="text-sm font-extrabold text-slate-900">
            Vista previa · Planilla FEDUP{view.label ? ` de ${view.label}` : ""}
          </h2>
          {updatedLabel ? <span className="text-[11px] font-normal text-slate-400">actualizada {updatedLabel}</span> : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {signaturesOn ? (
            <FedupSignatureModeSelector
              status={sig.status}
              mode={sig.mode}
              onToggleMode={sig.toggleMode}
              official={sig.official}
              disabled={!sig.status && sig.loadState === "loading"}
            />
          ) : null}

          <button
            type="button"
            onClick={reload}
            disabled={isLoading}
            aria-label="Actualizar"
            title="Actualizar: volver a generar la planilla con los datos actuales"
            className={ICON_BTN}
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} aria-hidden="true" />
          </button>

          {embedSupported ? (
            <div className="flex items-center gap-1" role="group" aria-label="Zoom">
              <button
                type="button"
                onClick={() => changeZoom((current) => stepZoom(current, -1))}
                disabled={zoom <= FEDUP_ZOOM_MIN || !showFrame}
                aria-label="Alejar"
                title="Alejar"
                className={ICON_BTN}
              >
                <FiMinus aria-hidden="true" />
              </button>
              <span className="w-11 select-none text-center text-[11px] font-semibold tabular-nums text-slate-500" title="Zoom (100 % = ancho de la ventana)">
                {Math.round(zoom * 100)} %
              </span>
              <button
                type="button"
                onClick={() => changeZoom((current) => stepZoom(current, 1))}
                disabled={zoom >= FEDUP_ZOOM_MAX || !showFrame}
                aria-label="Acercar"
                title="Acercar"
                className={ICON_BTN}
              >
                <FiPlus aria-hidden="true" />
              </button>
            </div>
          ) : null}

          <button type="button" onClick={() => onClose?.()} ref={closeRef} aria-label="Cerrar vista previa" title="Cerrar (Esc)" className={PRIMARY_BTN}>
            <FiX aria-hidden="true" />
            Cerrar
          </button>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 bg-slate-200">
        {/* Contenedor con scroll propio: siempre montado (así se mide el ancho y el scroll sobrevive a "Actualizar"). */}
        <div ref={scrollRef} data-fedup-preview-scroll="true" className="absolute inset-0 overflow-x-auto overflow-y-scroll bg-[#282828]">
          {showFrame ? (
            <div
              ref={frameWrapRef}
              className={signatures ? "relative mx-auto" : "mx-auto"}
              style={{ width: frame.width || "100%", height: frame.height || "100%" }}
            >
              <iframe
                key={frameEpoch ? `${url}#${frameEpoch}` : url}
                title="Vista previa de la planilla FEDUP"
                src={buildPreviewViewerSrc(url)}
                onLoad={handleFrameLoad}
                onMouseEnter={signatures ? () => (pointerVisitedFrameRef.current = true) : undefined}
                onFocus={signatures ? () => (pointerVisitedFrameRef.current = true) : undefined}
                className="block h-full w-full border-0 bg-white"
              />
              {useOverlay ? (
                <FedupSignatureOverlay
                  status={sig.status}
                  mode={activeMode}
                  map={pageMap}
                  frameHeight={frame.height}
                  official={sig.official}
                  previews={sig.previews}
                  onSelectSlot={sig.openSlot}
                  lockSlotId={sig.lockSlotId}
                  onDismissLock={sig.dismissLock}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        {usePanel ? (
          <FedupSignaturePanel
            status={sig.status}
            mode={activeMode}
            official={sig.official}
            previews={sig.previews}
            imageFor={sig.imageFor}
            ensureImage={sig.ensureImage}
            onSelectSlot={sig.openSlot}
            lockSlotId={sig.lockSlotId}
            onClose={() => sig.toggleMode(activeMode)}
          />
        ) : null}

        {activeMode && sig.loadState === "error" ? (
          <div role="alert" className="absolute inset-x-0 top-0 z-30 flex items-center justify-center gap-2 bg-rose-50/95 py-1.5 text-xs font-semibold text-rose-800">
            {SIGNATURE_UI_MESSAGES.status}
            <button type="button" onClick={sig.refresh} className="underline underline-offset-2">
              Reintentar
            </button>
          </div>
        ) : null}

        {showViewer && !embedSupported ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-200 px-6 text-center">
            <p className="max-w-md text-sm font-semibold text-slate-700">
              Este navegador no puede mostrar el PDF dentro de la página. Ábrelo en una pestaña nueva para revisarlo.
            </p>
            <a href={url} target="_blank" rel="noopener noreferrer" className={PRIMARY_BTN}>
              <FiExternalLink aria-hidden="true" />
              Abrir en pestaña nueva
            </a>
          </div>
        ) : null}

        {isLoading ? (
          <div
            role="status"
            aria-live="polite"
            className={[
              "absolute flex items-center justify-center gap-2 text-sm font-semibold text-slate-700",
              url ? "inset-x-0 top-0 z-10 bg-white/85 py-2 backdrop-blur-sm" : "inset-0 bg-slate-200",
            ].join(" ")}
          >
            <FiLoader className="animate-spin" aria-hidden="true" />
            {url ? "Actualizando…" : "Generando la planilla…"}
          </div>
        ) : null}

        {status === "error" && error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-200 px-6 text-center">
            <FiAlertTriangle className="text-2xl text-rose-600" aria-hidden="true" />
            <p role="alert" className="max-w-md text-sm font-semibold text-slate-800">
              {error.message}
            </p>
            <button type="button" onClick={reload} className={PRIMARY_BTN}>
              <FiRefreshCw aria-hidden="true" />
              Reintentar
            </button>
          </div>
        ) : null}
      </div>

      {signatures ? (
        <FedupSignaturePad
          slot={signaturesOn ? sig.padSlot : null}
          modeLabel={getModeInfo(sig.padSlot?.mode)?.label || ""}
          container={container}
          busy={sig.busy}
          error={sig.padError}
          locked={sig.padLocked}
          official={sig.official}
          imageUrl={sig.padSlot ? sig.imageFor(sig.padSlot.slot) : null}
          ensureImage={sig.ensureImage}
          onClose={sig.closePad}
          onSave={sig.save}
          onRemove={sig.remove}
        />
      ) : null}
    </>
  );
}

function FedupPreviewModal({ open, onClose, matchId, loadPdf, sport = "", container = null, signatures = null }) {
  return (
    <Modal
      open={Boolean(open)}
      onClose={() => onClose?.()}
      container={container || undefined}
      // El visor va por encima de la planilla (Modal de Joy a 1300) y de sus overlays.
      sx={{ zIndex: 1700 }}
      // Los eventos de React de un portal suben por el árbol del padre: se cortan para que la planilla no los vea.
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <ModalDialog
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        {...{ [FEDUP_PREVIEW_OPEN_ATTR]: "true" }}
        // Atajos de teclado de la planilla de vóley: ignorar todo lo que ocurra dentro del visor.
        data-volleyball-shortcuts-ignore="true"
        sx={{
          p: 0,
          gap: 0,
          overflow: "hidden",
          width: "min(96vw, 1500px)",
          maxWidth: "96vw",
          height: "min(94dvh, 1100px)",
          maxHeight: "94dvh",
          borderRadius: "14px",
        }}
      >
        <FedupPreviewContent
          onClose={onClose}
          matchId={matchId}
          loadPdf={loadPdf}
          sport={sport}
          signatures={signatures}
          container={container}
        />
      </ModalDialog>
    </Modal>
  );
}

export default memo(FedupPreviewModal);
